// Advanced Booking Types for Multi-Service, Waitlist, and Recurring Appointments

import { BookingServiceDetailDto } from './booking';

// Multi-Service Booking Types
export interface ServiceSelection {
  serviceId: number;
  serviceName: string;
  serviceCategory: string;
  durationMinutes: number;
  price: number;
  isSelected: boolean;
}

export interface MultiServiceBookingSummary {
  selectedServices: ServiceSelection[];
  totalDuration: number;
  totalPrice: number;
  estimatedEndTime?: string;
}

export interface CreateMultiServiceBookingDto {
  bookingDateTime: string;
  barberId: number;
  barbershopId: number;
  serviceIds: number[];
  notes?: string;
}

// Booking Modification Types
export interface BookingModificationDto {
  bookingId: number;
  bookingDateTime?: string;
  barberId?: number;
  serviceIds?: number[];
  serviceBundleId?: number | null;
  additionalServiceIds?: number[];
  notes?: string;
}

// GAP-7: BookingModificationHistory removed — direct modifications produce no
// history records on the backend, and the endpoint it modeled never existed.

// Cancellation Types (Enhanced)
export enum CancellationReason {
  ScheduleConflict = 'Schedule conflict',
  FoundAnotherBarbershop = 'Found another barbershop',
  NoLongerNeed = 'No longer need service',
  EmergencyIllness = 'Emergency/Illness',
  FinancialReasons = 'Financial reasons',
  WantToReschedule = 'Want to reschedule',
  Other = 'Other'
}

export interface BookingCancellationDto {
  bookingId: number;
  reason: CancellationReason | string;
  notifyClient?: boolean;
  allowRefund?: boolean;
}

// GAP-7: CancellationPolicyInfo removed — use CancellationFeePreview from
// types/policy.ts (backed by GET /policy-charges/preview/cancellation/{bookingId}).

// BOOK-3c: extended waitlist types (WaitlistPreferences, NotificationPreference,
// WaitlistStatusDisplay, JoinWaitlistDto) removed with the retired
// waitlistExtendedApi — the real surface lives in services/waitlistApi.ts.
export interface TimeRange {
  start: string; // HH:mm format
  end: string;   // HH:mm format
  label: string;  // e.g., "Morning", "Afternoon"
}

// Recurring Appointments Types (Extended from existing recurringBookingApi.ts)
export enum RecurrenceFrequency {
  Weekly = 'Weekly',
  BiWeekly = 'BiWeekly',
  Monthly = 'Monthly'
}

export interface RecurringAppointmentPreview {
  appointmentDate: string;
  appointmentTime: string;
  weekNumber: number;
  status: 'Scheduled' | 'Tentative' | 'Skipped';
}

export interface RecurringSeriesPreview {
  frequency: RecurrenceFrequency;
  nextAppointments: RecurringAppointmentPreview[];
  totalOccurrences: number;
  estimatedTotalCost: number;
}

export interface CreateRecurringAppointmentFormData {
  barberId: number;
  barbershopId: number;
  serviceIds: number[];
  frequency: RecurrenceFrequency;
  preferredDayOfWeek: number;
  preferredTime: string;
  startDate: string;
  endOption: 'endDate' | 'occurrences' | 'indefinite';
  endDate?: string;
  maxOccurrences?: number;
  notes?: string;
}

export interface RecurringAppointmentManagement {
  id: number;
  frequency: RecurrenceFrequency;
  frequencyDisplay: string;
  nextAppointmentDate?: string;
  totalGenerated: number;
  status: 'Active' | 'Paused' | 'Cancelled' | 'Completed';
  isPaused: boolean;
  pausedUntil?: string;
  services: BookingServiceDetailDto[];
  totalPrice: number;
  barberName: string;
  barbershopName: string;
  createdAt: string;
}

export interface RecurringAppointmentAction {
  type: 'pause' | 'resume' | 'skip' | 'cancel' | 'modify';
  appointmentId: number;
  pauseUntil?: string;
  reason?: string;
}

// Booking Flow State Types
export interface BookingFlowState {
  step: 'service' | 'datetime' | 'barber' | 'review' | 'confirm';
  selectedServices: ServiceSelection[];
  selectedDate?: string;
  selectedTime?: string;
  selectedBarberId?: number;
  isRecurring: boolean;
  recurringSettings?: CreateRecurringAppointmentFormData;
  notes?: string;
}

// Error and Validation Types
export interface BookingValidationError {
  field: string;
  message: string;
  code: string;
}

export interface BookingResponse {
  success: boolean;
  bookingId?: number;
  message?: string;
  errors?: BookingValidationError[];
  waitlistId?: number; // If slot unavailable, created waitlist entry
}
